import { createSlice } from '@reduxjs/toolkit';
import { DefaultSettings, ROBOTS } from '../utils/constants';
import { formatBoardRevision, formatDeviceUidHex } from '../utils/deviceFormatters';
import { formatFirmwareVersionOrUndefined } from '../utils/formatFirmwareVersion';
import { robotSlice } from './RobotSlice';
import type { RootState } from './Store';

export type FirmwareInstallProgress = {
  deviceIndex: number;
  deviceType: string | null;
  progress: number;
  updating: boolean;
};

export type RemoteDeviceSystemInfo = {
  deviceIndex: number;
  deviceType: string | null;
  isConnected: boolean;
  firmwareBootloader: string;
  firmwareMainApp: string;
  boardRevision: number | null;
  devicePartNumber: string | null;
  uid: string | null;
  updateInProgress: boolean;
  updateProgress: number;
};

type RemoteDeviceSystemInfoPayload = {
  deviceIndex?: number;
  deviceType?: string | null;
  isConnected?: boolean;
  firmwareBootloader?: string | null;
  firmwareMainApp?: string | null;
  boardRevision?: unknown;
  devicePartNumber?: string | null;
  uid?: number[];
  uidSize?: number;
  updateInProgress?: boolean;
  updateProgress?: number;
  blMajor?: number | null;
  blMinor?: number | null;
  blRevision?: number | null;
  maMajor?: number | null;
  maMinor?: number | null;
  maRevision?: number | null;
};

function mergeRemoteDeviceSystemInfo(previous: RemoteDeviceSystemInfo | undefined, payload: RemoteDeviceSystemInfoPayload): RemoteDeviceSystemInfo {
  const firmwareBootloader =
    payload.firmwareBootloader ??
    formatFirmwareVersionOrUndefined(payload.blMajor, payload.blMinor, payload.blRevision) ??
    previous?.firmwareBootloader ??
    '';

  const firmwareMainApp =
    payload.firmwareMainApp ??
    formatFirmwareVersionOrUndefined(payload.maMajor, payload.maMinor, payload.maRevision) ??
    previous?.firmwareMainApp ??
    '';

  const formattedBoardRevision = payload.boardRevision != null ? formatBoardRevision(payload.boardRevision) : null;
  const uid = payload.uid !== undefined ? formatDeviceUidHex(payload.uid, payload.uidSize) ?? previous?.uid ?? '' : previous?.uid ?? '';

  return {
    deviceIndex: payload.deviceIndex ?? previous?.deviceIndex ?? 0,
    deviceType: payload.deviceType ?? previous?.deviceType ?? null,
    isConnected: payload.isConnected ?? previous?.isConnected ?? false,
    firmwareBootloader,
    firmwareMainApp,
    boardRevision: formattedBoardRevision ?? previous?.boardRevision ?? null,
    devicePartNumber: payload.devicePartNumber ?? previous?.devicePartNumber ?? null,
    uid,
    updateInProgress: payload.updateInProgress ?? previous?.updateInProgress ?? false,
    updateProgress: payload.updateProgress ?? previous?.updateProgress ?? 0,
  };
}

// This reducer is to track information about connected robot
const initialState = {
  robotType: ROBOTS.NONE,
  robotName: '',
  bluetoothAdvertisingName: '',
  boardRevision: null as number | null,
  uid: '' as string,
  sku: '',
  firmwarePn: '',
  firmwareBootloader: '',
  currentApplicationId: '',
  robotColor: DefaultSettings.color,
  userRobotColor: DefaultSettings.color, // color used outside of programs when driving,
  sensorData: DefaultSettings.sensorData, // whether or not sensor data is enabled
  joystick: DefaultSettings.joystick, // May not use, for joystick vs keyboard driving,
  userRobotSpeed: DefaultSettings.speed, // speed setting for driving vs. in programs,
  previousRobot: DefaultSettings.previousRobot,
  // Normalized firmware workflow payload from RobotFirmwareManager.
  firmwareVersions: null as unknown as Record<string, unknown> | null,
  // Full drive-unit firmware metadata payload from getRobotFirmwareVersions().
  driveUnitFirmwareVersions: null as unknown as Record<string, unknown> | null,
  firmwareInstallProgress: [] as FirmwareInstallProgress[],
  remoteDeviceSystemInfo: [] as RemoteDeviceSystemInfo[],
  // Electronics/remote devices per port (1-8).
  electronics: [] as Array<{ electronicType: string | null; info?: Record<string, unknown> }>,
  // True after the initial getRemoteDeviceTypes response populates drive-unit port types.
  remoteDeviceTypesReady: false,
  // Beam break sensor data per port. From getBeamBreakSensorSettings response.
  beamBreakSensorData: {} as Record<number, { port: number; distance: number; objectPresent: boolean; distanceThreshold?: number }>,
  // Distance sensor data per port. From getDistanceSensorSettings response.
  distanceSensorData: {} as Record<number, { port: number; distance: number; objectPresent: boolean; distanceThreshold: number }>,
  // Magnetic switch data per port. From getMagneticSwitchSettings response.
  magneticSwitchData: {} as Record<number, { port: number; left_switch: boolean; right_switch: boolean }>,
  // Bee adapter data per port. From getBeeAdapterSettings response.
  beeAdapterData: {} as Record<number, { port: number; output: number }>,
  // Python program slots on the drive unit (slot1–slot8). Keyed by slot name matching the firmware asset ID.
  slotStorage: Object.fromEntries(
    ['slot1', 'slot2', 'slot3', 'slot4', 'slot5', 'slot6', 'slot7', 'slot8'].map((slotName) => [
      slotName,
      {
        slotNumber: Number(slotName.slice(4)),
        programTitle: '',
        isEmpty: true,
        /** BPS project id last deployed to this slot from the app (used for overwrite confirmation). */
        bpsProjectId: undefined as string | undefined,
        /** Project lastModified value embedded into the deployed source, normalized to UTC ms since epoch. */
        bpsProjectLastModifiedUtcMs: undefined as number | undefined,
        /** Filename on device from last metadata / deploy; used to preserve bpsProjectId across refreshes for the same robot. */
        deviceFilename: undefined as string | undefined,
      },
    ])
  ) as Record<
    string,
    {
      slotNumber: number;
      programTitle: string;
      isEmpty: boolean;
      bpsProjectId: string | undefined;
      bpsProjectLastModifiedUtcMs: number | undefined;
      deviceFilename: string | undefined;
    }
  >,
  /** Normalized UID for the robot that owns the cached slot provenance data. */
  slotStorageOwnerUid: null as string | null,
  // True after slot actions can proceed for the current connection.
  slotStorageReady: false,
  // True only when the current connection successfully refreshed or recovered slot metadata.
  slotStorageFresh: false,
};

export const robotDataSlice = createSlice({
  name: 'robotData',
  initialState,
  reducers: {
    onUpdateRobotType: (state, { payload }) => {
      state.robotType = payload;
    },
    onClearRobotInfo: (state) => ({
      ...state,
      uid: initialState.uid,
      boardRevision: initialState.boardRevision,
      sku: initialState.sku,
      firmwarePn: initialState.firmwarePn,
      firmwareBootloader: initialState.firmwareBootloader,
      currentApplicationId: initialState.currentApplicationId,
      bluetoothAdvertisingName: initialState.bluetoothAdvertisingName,
      firmwareVersions: initialState.firmwareVersions,
      driveUnitFirmwareVersions: initialState.driveUnitFirmwareVersions,
      firmwareInstallProgress: initialState.firmwareInstallProgress,
      remoteDeviceSystemInfo: initialState.remoteDeviceSystemInfo,
      electronics: initialState.electronics,
      remoteDeviceTypesReady: initialState.remoteDeviceTypesReady,
      beamBreakSensorData: initialState.beamBreakSensorData,
      distanceSensorData: initialState.distanceSensorData,
      magneticSwitchData: initialState.magneticSwitchData,
      beeAdapterData: initialState.beeAdapterData,
      slotStorage: Object.fromEntries(
        Object.entries(state.slotStorage).map(([key, slot]) => [
          key,
          {
            ...initialState.slotStorage[key],
            bpsProjectId: slot.bpsProjectId,
            bpsProjectLastModifiedUtcMs: slot.bpsProjectLastModifiedUtcMs,
            deviceFilename: slot.deviceFilename,
          },
        ])
      ) as typeof initialState.slotStorage,
      slotStorageOwnerUid: state.slotStorageOwnerUid,
      slotStorageReady: initialState.slotStorageReady,
      slotStorageFresh: initialState.slotStorageFresh,
    }),
    onUpdateBluetoothAdvertisingName: (state, { payload }) => {
      state.bluetoothAdvertisingName = payload || '';
    },
    onUpdateRobotBoardRevision: (state, { payload }) => {
      state.boardRevision = payload;
    },
    onUpdateRobotUid: (state, { payload }) => {
      state.uid = formatDeviceUidHex(payload, payload?.length ?? 0) || '';
    },
    onUpdateSlotStorageOwnerUid: (state, { payload }) => {
      if (payload == null || payload === '') {
        state.slotStorageOwnerUid = null;
        return;
      }
      if (Array.isArray(payload)) {
        state.slotStorageOwnerUid = formatDeviceUidHex(payload, payload.length) ?? null;
        return;
      }
      const text = String(payload).trim();
      state.slotStorageOwnerUid = text || null;
    },
    onUpdateRobotSku: (state, { payload }) => {
      state.sku = payload;
    },
    onUpdateRobotFirmwarePn: (state, { payload }) => {
      state.firmwarePn = payload;
    },
    onUpdateRobotFirmwareBootloader: (state, { payload }) => {
      state.firmwareBootloader = payload;
    },
    onUpdateRobotCurrentApplicationId: (state, { payload }) => {
      state.currentApplicationId = payload;
    },
    onUpdateRobotColor: (state, { payload }) => {
      state.robotColor = { r: payload.r, g: payload.g, b: payload.b };
    },
    onUpdateUserRobotColor: (state, { payload }) => {
      state.userRobotColor = {
        r: payload.r,
        g: payload.g,
        b: payload.b,
      };
    },
    onUpdateSensorData: (state, { payload }) => ({ ...state, sensorData: payload }),
    onUpdateUserSpeed: (state, { payload }) => {
      state.userRobotSpeed = payload;
    },
    onUpdatePreviousRobot: (state, { payload }) => {
      state.previousRobot = payload;
    },
    onUpdateFirmwareVersions: (state, { payload }) => {
      state.firmwareVersions = payload;
    },
    onUpdateDriveUnitFirmwareVersions: (state, { payload }) => {
      state.driveUnitFirmwareVersions = payload;
    },
    onUpdateRemoteDeviceSystemInfo: (state, { payload }) => {
      if (!payload) return;
      const typedPayload = payload as RemoteDeviceSystemInfoPayload;
      const existing = state.remoteDeviceSystemInfo.find((p) => p.deviceIndex === payload?.deviceIndex);
      if (existing) {
        Object.assign(existing, mergeRemoteDeviceSystemInfo(existing, typedPayload));
      } else {
        state.remoteDeviceSystemInfo.push(mergeRemoteDeviceSystemInfo(undefined, typedPayload));
      }
    },
    onUpdateElectronics: (state, { payload }) => {
      state.electronics = Array.isArray(payload) ? payload : [];
    },
    /** Update port types; clears stale info when a previously identified accessory type changes. */
    onUpdateElectronicsPortTypes: (state, { payload }) => {
      const types = Array.isArray(payload) ? payload : [];
      for (let i = 0; i < 8; i++) {
        const type = types[i];
        if (state.electronics[i] == null) {
          state.electronics[i] = { electronicType: type ?? null, info: {} };
        } else {
          const previousType = state.electronics[i].electronicType ?? null;
          const nextType = type ?? null;
          state.electronics[i].electronicType = nextType;
          if (previousType !== nextType && previousType != null) {
            state.electronics[i].info = {};
          }
        }
      }
      if (Array.isArray(payload)) {
        state.remoteDeviceTypesReady = true;
      }
    },
    onUpdateFirmwareInstallProgress: (state, { payload }) => {
      if (payload?.deviceIndex == null) {
        return;
      }
      const existing = state.firmwareInstallProgress.find((p) => p.deviceIndex === payload?.deviceIndex);
      if (existing) {
        existing.progress = payload?.progress ?? 0;
        existing.updating = payload?.updating ?? false;
        existing.deviceType = payload?.deviceType ?? null;
      } else {
        state.firmwareInstallProgress.push({
          deviceIndex: payload.deviceIndex,
          deviceType: payload?.deviceType ?? null,
          progress: payload?.progress ?? 0,
          updating: payload?.updating ?? false,
        });
      }
    },
    onUpdateBeamBreakSensorData: (state, { payload }) => {
      const { port, data } = payload;
      if (port != null && data != null) {
        state.beamBreakSensorData[port] = data;
      }
    },
    onUpdateDistanceSensorData: (state, { payload }) => {
      const { port, data } = payload;
      if (port != null && data != null) {
        state.distanceSensorData[port] = data;
      }
    },
    onUpdateMagneticSwitchData: (state, { payload }) => {
      const { port, data } = payload;
      if (port != null && data != null) {
        state.magneticSwitchData[port] = data;
      }
    },
    onUpdateBeeAdapterData: (state, { payload }) => {
      const { port, data } = payload;
      if (port != null && data != null) {
        state.beeAdapterData[port] = data;
      }
    },
    onUpdateSlotStorage: (state, { payload }) => {
      if (!payload || typeof payload !== 'object') return;

      // Single-slot update: payload has a slotNumber field.
      if (typeof payload.slotNumber === 'number') {
        const key = `slot${payload.slotNumber}`;
        if (!state.slotStorage[key]) return;
        state.slotStorage[key] = { ...state.slotStorage[key], ...payload };
        return;
      }

      // Full replacement: payload is an object keyed by slot name (slot1–slot8).
      state.slotStorage = payload;
    },
    onSlotStorageQueryStarted: (state) => {
      state.slotStorageReady = false;
      state.slotStorageFresh = false;
    },
    onSlotStorageReady: (state, { payload }) => {
      state.slotStorageReady = true;
      state.slotStorageFresh = payload !== false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(robotSlice.actions.onConnect, (state, { payload }) => ({
        ...state,
        robotName: '',
        robotType: payload.robotType,
        remoteDeviceTypesReady: initialState.remoteDeviceTypesReady,
        slotStorageReady: state.slotStorageReady,
        slotStorageFresh: state.slotStorageFresh,
      }))
      .addCase(robotSlice.actions.onDisconnect, (state) => ({
        ...state,
        robotName: '',
        robotType: ROBOTS.NONE,
        bluetoothAdvertisingName: '',
        electronics: [],
        remoteDeviceTypesReady: initialState.remoteDeviceTypesReady,
        driveUnitFirmwareVersions: initialState.driveUnitFirmwareVersions,
        remoteDeviceSystemInfo: initialState.remoteDeviceSystemInfo,
        firmwareInstallProgress: initialState.firmwareInstallProgress,
        beamBreakSensorData: {},
        distanceSensorData: {},
        magneticSwitchData: {},
        beeAdapterData: {},
        slotStorage: Object.fromEntries(
          Object.entries(state.slotStorage).map(([key, slot]) => [
            key,
            {
              ...initialState.slotStorage[key],
              bpsProjectId: slot.bpsProjectId,
              bpsProjectLastModifiedUtcMs: slot.bpsProjectLastModifiedUtcMs,
              deviceFilename: slot.deviceFilename,
            },
          ])
        ) as typeof initialState.slotStorage,
        slotStorageOwnerUid: state.slotStorageOwnerUid,
        slotStorageReady: initialState.slotStorageReady,
        slotStorageFresh: initialState.slotStorageFresh,
      }));
  },
});

export const selectRobotData = (state: RootState) => {
  const { userRobotColor, robotType, sensorData } = state.robotData;
  return {
    userRobotColor,
    robotType,
    sensorData,
  };
};

export const selectUserDrivingData = (state: RootState) => {
  const { userRobotSpeed, userRobotColor, robotType } = state.robotData;
  return {
    userRobotSpeed,
    userRobotColor,
    robotType,
  };
};

export default robotDataSlice.reducer;
